import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DropdownMenuItem } from '@c2fo/liquidity';
import { EyeIcon, LockAltIcon } from '@c2fo/liquidity/icons';
import { ServiceAccount } from '../../api/ServiceAccounts';
import { ChangePasswordModal } from '../ChangePasswordModal';
import { useServiceAccountsState } from '../../contexts/FileDeliveryContext';
import { useErrorState } from '../ErrorBanner';
import { DataGrid, DataGridCard } from '../DataGrid';
import { ActionsMenu } from '../ActionsMenu';
import { formatDateWithTimezone } from '../../../../utils/dateFormat';

interface ServiceAccountsGridProps {
  accounts: ServiceAccount[];
}

export const ServiceAccountsGrid: React.FC<ServiceAccountsGridProps> = ({ accounts }) => {
  const navigate = useNavigate();
  const { updatePassword } = useServiceAccountsState();
  const { addError } = useErrorState();
  const [passwordModalAccount, setPasswordModalAccount] = useState<ServiceAccount | null>(null);

  return (
    <>
      <ChangePasswordModal
        isOpen={!!passwordModalAccount}
        accountName={passwordModalAccount?.name ?? ''}
        accountDisplayName={passwordModalAccount?.displayName ?? ''}
        onClose={() => setPasswordModalAccount(null)}
        onSave={updatePassword}
        onError={(message) => addError({ id: 'reset-password-error', message, type: 'error' })}
      />
      <DataGrid>
        {accounts.map((account) => (
          <DataGridCard key={account.uid}>
            <div className="flex items-center justify-end">
              <ActionsMenu>
                <DropdownMenuItem
                  className="active:bg-lightBlue-100"
                  onSelect={() => navigate(`/file-delivery/serviceAccounts/${account.uid}`)}
                >
                  <EyeIcon className="h-4 w-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem className="active:bg-lightBlue-100" onSelect={() => setPasswordModalAccount(account)}>
                  <LockAltIcon className="h-4 w-4 mr-2" />
                  Reset Password
                </DropdownMenuItem>
              </ActionsMenu>
            </div>

            <div>
              <span className="text-sm font-medium text-gray-900">{account.displayName}</span>
              {account.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{account.description}</p>}
            </div>

            <div className="flex flex-col gap-1 text-xs text-gray-500">
              <div>
                <span className="font-medium">Created:</span>{' '}
                {account.createTime ? formatDateWithTimezone(account.createTime) : '-'}
              </div>
              <div>
                <span className="font-medium">Updated:</span>{' '}
                {account.updateTime ? formatDateWithTimezone(account.updateTime) : '-'}
              </div>
            </div>
          </DataGridCard>
        ))}
      </DataGrid>
    </>
  );
};
